import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WizardStepAuth } from './wizard-step-auth';
import {
    createDefaultFormData,
    fromIntegrationManifest,
    toIntegrationManifest,
    type AuthFormData,
} from '@/types/integration-form-types';

interface IntegrationAuthTabProps {
    typeConfig: Record<string, unknown>;
    onSave: (updates: Record<string, unknown>) => void;
    isSaving?: boolean;
    onDeferredDirtyChange?: (payload: { dirty: boolean; patch: Record<string, unknown> }) => void;
    disabled?: boolean;
}

export function IntegrationAuthTab({
    typeConfig,
    onDeferredDirtyChange,
    disabled = false,
}: IntegrationAuthTabProps) {
    const formData = useMemo(() => fromIntegrationManifest(typeConfig), [typeConfig]);
    const [authForm, setAuthForm] = useState<AuthFormData>(formData.auth);
    const lastDirtyPayloadRef = useRef<string | null>(null);

    useEffect(() => {
        setAuthForm((prev) => {
            if (JSON.stringify(prev) === JSON.stringify(formData.auth)) return prev;
            return formData.auth;
        });
    }, [formData.auth]);

    const baselineSerialized = useMemo(() => JSON.stringify(formData.auth), [formData.auth]);
    const currentSerialized = useMemo(() => JSON.stringify(authForm), [authForm]);
    const isDirty = baselineSerialized !== currentSerialized;

    const patch = useMemo(() => {
        const fullForm = { ...createDefaultFormData(), ...formData, auth: authForm };
        const { manifest } = toIntegrationManifest(fullForm);
        return {
            type_config: {
                ...typeConfig,
                auth: manifest.auth,
            },
        };
    }, [formData, authForm, typeConfig]);

    useEffect(() => {
        if (!onDeferredDirtyChange) return;
        const payload = { dirty: isDirty, patch: isDirty ? patch : {} };
        const serialized = JSON.stringify(payload);
        if (lastDirtyPayloadRef.current === serialized) return;
        lastDirtyPayloadRef.current = serialized;
        onDeferredDirtyChange(payload);
    }, [isDirty, patch, onDeferredDirtyChange]);

    return (
        <div className="space-y-4">
            <Card className="border-border/50 p-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Authorization Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                    <WizardStepAuth
                        value={authForm}
                        onChange={(next) => {
                            if (disabled) return;
                            setAuthForm(next);
                        }}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
